/**
 * Settings → UI → "Toolbar" (F01-P3).
 *
 * Jean-Luc's ask was "put the compare group on the toolbar, or let me pick the
 * items". This is the pick-the-items half: the top list is the toolbar row
 * (drag to reorder, capped), the middle list is the More ▾ menu (tick an item
 * to promote it), and anything hidden collects at the bottom with a Restore.
 *
 * All the rules live in `toolbarCustomizationOps`; this file wires them to the
 * store and to analytics, and surfaces the one thing the ops cannot: what was
 * repaired in a stored layout that broke the anchor policy.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { useAppStore } from '@/store/appStore';
import { useToolbarGating } from '../../../hooks/useToolbarGating';
import { unavailableToolbarItems } from '../../../model/toolbarItemCatalog';
import { MORE_GROUP_LABELS, type ToolbarItemId } from '../../../model/toolbarItemTypes';
import {
  TOOLBAR_PRIMARY_CAP,
  TOOLBAR_REPAIR_MESSAGES,
  normalizeToolbarCustomization,
  resolveToolbarBuckets,
  type ToolbarCustomization,
  type ToolbarRepairCode,
} from '../../../model/toolbarCustomization';
import {
  canPromoteToolbarItem,
  demoteToolbarItem,
  hideToolbarItem,
  promoteToolbarItem,
  reorderToolbarPrimary,
  restoreToolbarItem,
} from '../../../model/toolbarCustomizationOps';
import { ToolbarCustomizationRow } from './ToolbarCustomizationRow';
import { ToolbarRowList } from './ToolbarRowList';

type Edit = (
  current: ToolbarCustomization | null,
  id: ToolbarItemId,
) => ToolbarCustomization | null;

export function ToolbarCustomizationSection() {
  const stored = useAppStore((state) => state.toolbarCustomization);
  const setStored = useAppStore((state) => state.setToolbarCustomization);
  const { track } = useAnalytics();
  const [repairs, setRepairs] = useState<ToolbarRepairCode[]>([]);
  const healed = useRef(false);

  // A layout that broke the anchor policy is repaired rather than discarded
  // (see `toolbarCustomization.ts`). Healing storage once, on open, keeps the
  // explanation from reappearing every time the panel is visited.
  useEffect(() => {
    if (healed.current) return;
    healed.current = true;
    const result = normalizeToolbarCustomization(stored);
    if (result.repairs.length === 0) return;
    setRepairs(result.repairs);
    setStored(result.customization);
  }, [stored, setStored]);

  const buckets = resolveToolbarBuckets(stored);
  const canPromote = canPromoteToolbarItem(stored);
  // ~10 items are engine- or connection-gated. Ticking one of those with no
  // eligible connection open updates the store and shows nothing on the
  // toolbar, which reads as a broken checkbox unless the row says otherwise.
  const { gating } = useToolbarGating();
  const unavailable = useMemo(() => unavailableToolbarItems(gating), [gating]);

  const apply = (action: string, edit: Edit) => (id: ToolbarItemId) => {
    const next = edit(stored, id);
    if (next === stored) return; // refused (anchor, or the row is full)
    setStored(next);
    track('feature:toolbar_customized', { action, itemId: id });
  };

  const onToggleRow = (id: ToolbarItemId) =>
    buckets.primary.includes(id)
      ? apply('demote', demoteToolbarItem)(id)
      : apply('promote', promoteToolbarItem)(id);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">Toolbar</div>
        {stored && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs"
            onClick={() => {
              setStored(null);
              setRepairs([]);
              track('feature:toolbar_customized', { action: 'reset', itemId: 'all' });
            }}
            data-testid="toolbar-customize-reset"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            Reset to defaults
          </Button>
        )}
      </div>

      {repairs.length > 0 && (
        <div
          className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-foreground"
          data-testid="toolbar-customize-repairs"
        >
          <div className="font-medium">Your saved toolbar layout needed a small fix</div>
          <ul className="mt-1 list-disc space-y-0.5 pl-4 text-muted-foreground">
            {repairs.map((code) => (
              <li key={code}>{TOOLBAR_REPAIR_MESSAGES[code]}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-baseline justify-between">
        <div className="text-xs font-medium text-muted-foreground">On the toolbar</div>
        <div className="text-xs text-muted-foreground" data-testid="toolbar-customize-count">
          {buckets.primary.length} of {TOOLBAR_PRIMARY_CAP} slots
          {!canPromote && ' — full, hide or move something off to add more'}
        </div>
      </div>
      <ToolbarRowList
        items={buckets.primary}
        unavailable={unavailable}
        onReorder={(activeId, overId) => {
          const next = reorderToolbarPrimary(stored, activeId, overId);
          if (next === stored) return;
          setStored(next);
          track('feature:toolbar_customized', { action: 'reorder', itemId: activeId });
        }}
        onToggleRow={onToggleRow}
        onHide={apply('hide', hideToolbarItem)}
      />

      <div className="text-xs font-medium text-muted-foreground">
        In the More ▾ menu — tick one to move it onto the toolbar
      </div>
      <div className="rounded-md border border-border bg-background p-1">
        {buckets.more.map((group) => (
          <div key={group.group} data-testid={`toolbar-customize-group-${group.group}`}>
            <div className="px-2 pt-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
              {MORE_GROUP_LABELS[group.group]}
            </div>
            {group.items.map((id) => (
              <ToolbarCustomizationRow
                key={id}
                id={id}
                placement="more"
                canPromote={canPromote}
                unavailable={unavailable.has(id)}
                onToggleRow={onToggleRow}
                onHide={apply('hide', hideToolbarItem)}
                onRestore={apply('restore', restoreToolbarItem)}
              />
            ))}
          </div>
        ))}
      </div>

      {buckets.hidden.length > 0 && (
        <>
          <div className="text-xs font-medium text-muted-foreground">
            Hidden — not on the toolbar or in the More ▾ menu
          </div>
          <div
            className="rounded-md border border-border bg-background p-1"
            data-testid="toolbar-customize-hidden-list"
          >
            {buckets.hidden.map((id) => (
              <ToolbarCustomizationRow
                key={id}
                id={id}
                placement="hidden"
                canPromote={canPromote}
                unavailable={unavailable.has(id)}
                onToggleRow={onToggleRow}
                onHide={apply('hide', hideToolbarItem)}
                onRestore={apply('restore', restoreToolbarItem)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
